/**
 * src/lib/utils.ts
 * ------------------
 * General-purpose utility functions shared across the frontend.
 *
 * Fully implemented this will contain:
 *   - cn(...classes) — merges Tailwind classes using clsx + tailwind-merge
 *     to safely combine conditional and dynamic class strings.
 *   - formatRelativeTime(date) — converts a Date/ISO string to a
 *     human-readable relative label ("just now", "3 min ago", "2 days ago").
 *   - truncate(str, maxLen) — truncates a string with an ellipsis.
 *   - sleep(ms) — Promise-based delay for async flows.
 *   - debounce(fn, ms) — debounces a function call.
 *   - generateId() — returns a short random ID string.
 *
 * Key dependencies: clsx, tailwind-merge
 */
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

/** Merge Tailwind class strings safely (handles conditional classes). */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

/** Format a date as a relative human-readable string. */
export function formatRelativeTime(date: string | Date): string {
  const ms = Date.now() - new Date(date).getTime()
  const seconds = Math.floor(ms / 1000)
  if (seconds < 10)  return 'just now'
  if (seconds < 60)  return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60)  return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24)    return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7)      return `${days}d ago`
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(date))
}

/** Truncate a string to maxLen characters, appending an ellipsis. */
export function truncate(str: string, maxLen: number): string {
  if (str.length <= maxLen) return str
  return str.slice(0, maxLen - 1) + '…'
}

/** Promise-based delay. */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** Debounce a function — returns a new function that delays invocation. */
export function debounce<T extends (...args: unknown[]) => void>(
  fn: T,
  ms: number,
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout> | null = null
  return (...args) => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => fn(...args), ms)
  }
}

/** Generate a short random ID (not cryptographically secure). */
export function generateId(): string {
  return Math.random().toString(36).slice(2, 10)
}
